import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Circle, CircleCheck, CircleDashed, RefreshCw } from 'lucide-react';
import type { AgentStep } from '../api/schemas';
import { riseSx, shimmerTextSx, spinSx } from './motion';

type Status = AgentStep['status'] | 'pending';

type Row = { id: string; label: string; status: Status; detail?: string | undefined };

// The usual path through a question, so the steps still to come show before the server reaches them.
const PLAN: readonly Omit<Row, 'status'>[] = [
  { id: 'understand', label: 'Understanding the question' },
  { id: 'forecast', label: 'Checking the forecast table' },
  { id: 'search', label: 'Searching research' },
  { id: 'write', label: 'Writing the answer' },
  { id: 'check', label: 'Checking each claim against its source' },
];
const PLANNED = new Set(PLAN.map((p) => p.id));

/** The steps so far, then the planned ones not reached yet. A question the research agent handles takes its own
 * route, so its steps show as they come. */
function withPlan(steps: AgentStep[]): Row[] {
  if (steps.some((s) => !PLANNED.has(s.id))) return steps;
  const seen = new Set(steps.map((s) => s.id));
  return [...steps, ...PLAN.filter((p) => !seen.has(p.id)).map((p) => ({ ...p, status: 'pending' as const }))];
}

function StepIcon({ status }: { status: Status }) {
  if (status === 'active') return <Box component="span" sx={{ display: 'flex', color: 'primary.main', ...spinSx }}><RefreshCw size={15} /></Box>;
  if (status === 'done') return <Box component="span" sx={{ display: 'flex', color: 'success.main' }}><CircleCheck size={15} /></Box>;
  const Icon = status === 'skipped' ? CircleDashed : Circle;
  return <Box component="span" sx={{ display: 'flex', color: 'text.disabled' }}><Icon size={15} /></Box>;
}

/** The visible agent trace the PRD asks for: done, in progress and still to come. */
export function AgentSteps({ steps, showPlanned = false }: { steps: AgentStep[]; showPlanned?: boolean }) {
  const rows: Row[] = showPlanned ? withPlan(steps) : steps;
  return (
    <Stack component="ol" aria-label="Assistant steps" sx={{ listStyle: 'none', p: 0, m: 0, gap: 0.75 }}>
      {rows.map((step) => (
        <Stack component="li" key={step.id} direction="row" sx={{ gap: 1.25, alignItems: 'center', ...riseSx }}>
          <StepIcon status={step.status} />
          <Typography
            variant="body2"
            sx={[
              { fontSize: '0.8125rem', color: step.status === 'pending' || step.status === 'skipped' ? 'text.disabled' : 'text.secondary' },
              // A light sweep across the step in progress, so the work reads as live.
              step.status === 'active' && shimmerTextSx,
            ]}
          >
            {step.label}
            {step.detail ? `: ${step.detail}` : ''}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}
